import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';

const GridBackground = () => {
  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#f8fafc',
        backgroundImage: `linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)`,
        backgroundSize: '48px 48px',
        opacity: 0.4,
      }}
    />
  );
};

const ProgressBar = ({ progress, color }: { progress: number; color: string }) => {
  return (
    <div style={{ width: '100%', height: 8, backgroundColor: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
      <div
        style={{
          width: `${progress * 100}%`,
          height: '100%',
          backgroundColor: color,
          borderRadius: 4,
        }}
      />
    </div>
  );
};

const FeatureCard = ({ icon, title, delay, frame, fps }: { icon: string; title: string; delay: number; frame: number; fps: number }) => {
  const entryProgress = spring({ frame: frame - delay, fps, config: { damping: 12 } });
  const scale = interpolate(entryProgress, [0, 1], [0.8, 1]);
  const opacity = interpolate(entryProgress, [0, 1], [0, 1]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        backgroundColor: 'white',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        transform: `scale(${scale})`,
        opacity,
      }}
    >
      <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
        {icon}
      </div>
      <span style={{ fontSize: 16, fontWeight: 500, color: '#0f172a' }}>{title}</span>
      <div style={{ marginLeft: 'auto' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
    </div>
  );
};

export const SaasOnboardingDemo = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // Animation phases
  const welcomeEntry = spring({ frame, fps, config: { damping: 15 } });
  const progressStart = 60;
  const progressValue = interpolate(frame, [progressStart, progressStart + 90], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  const ctaEntry = spring({ frame: frame - 200, fps, config: { damping: 12 } });
  const ctaScale = interpolate(ctaEntry, [0, 1], [0.9, 1]);
  const ctaOpacity = interpolate(ctaEntry, [0, 1], [0, 1]);

  // Floating animation
  const float = Math.sin(frame * 0.05) * 5;

  return (
    <AbsoluteFill style={{ fontFamily: 'system-ui, sans-serif', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
      <GridBackground />

      {/* Main content */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: `translate(-50%, -50%) translateY(${float}px)`,
          width: width * 0.6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 32,
        }}
      >
        {/* Company Logo Placeholder */}
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: 20,
            backgroundColor: '#0f172a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: interpolate(welcomeEntry, [0, 1], [0, 1]),
            transform: `scale(${interpolate(welcomeEntry, [0, 1], [0.5, 1])})`,
          }}
        >
          <span style={{ color: 'white', fontSize: 32, fontWeight: 700 }}>A</span>
        </div>

        {/* Welcome Text */}
        <div
          style={{
            textAlign: 'center',
            opacity: interpolate(welcomeEntry, [0, 1], [0, 1]),
            transform: `translateY(${interpolate(welcomeEntry, [0, 1], [20, 0])}px)`,
          }}
        >
          <h1 style={{ fontSize: 48, fontWeight: 700, color: '#0f172a', margin: 0, marginBottom: 8 }}>
            Welcome, Sarah!
          </h1>
          <p style={{ fontSize: 20, color: '#64748b', margin: 0 }}>
            Let's get you set up with Acme Inc
          </p>
        </div>

        {/* Progress Section */}
        <div
          style={{
            width: '100%',
            padding: 24,
            backgroundColor: 'white',
            borderRadius: 16,
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Setup Progress</span>
            <span style={{ fontSize: 14, color: '#64748b' }}>{Math.round(progressValue * 100)}%</span>
          </div>
          <ProgressBar progress={progressValue} color="#22c55e" />
        </div>

        {/* Feature Cards */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <FeatureCard icon="📊" title="Dashboard Overview" delay={90} frame={frame} fps={fps} />
          <FeatureCard icon="👥" title="Team Collaboration" delay={110} frame={frame} fps={fps} />
          <FeatureCard icon="🔔" title="Smart Notifications" delay={130} frame={frame} fps={fps} />
          <FeatureCard icon="📈" title="Analytics & Reports" delay={150} frame={frame} fps={fps} />
        </div>

        {/* CTA Button */}
        <button
          style={{
            padding: '16px 48px',
            backgroundColor: '#0f172a',
            color: 'white',
            border: 'none',
            borderRadius: 12,
            fontSize: 18,
            fontWeight: 600,
            cursor: 'pointer',
            transform: `scale(${ctaScale})`,
            opacity: ctaOpacity,
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
          }}
        >
          Start Your Journey →
        </button>
      </div>

      {/* Decorative elements */}
      <div
        style={{
          position: 'absolute',
          top: height * 0.1,
          left: width * 0.1,
          width: 200,
          height: 200,
          borderRadius: '50%',
          backgroundColor: '#22c55e',
          opacity: 0.1,
          filter: 'blur(60px)',
          transform: `translateY(${Math.sin(frame * 0.03) * 20}px)`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: height * 0.15,
          right: width * 0.1,
          width: 250,
          height: 250,
          borderRadius: '50%',
          backgroundColor: '#3b82f6',
          opacity: 0.1,
          filter: 'blur(60px)',
          transform: `translateY(${Math.cos(frame * 0.03) * 20}px)`,
        }}
      />
    </AbsoluteFill>
  );
};
